import React, { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Backbone from 'backbone';

/* ui-connect */
import { Icon } from 'ui-connect';
import { ChartsBar } from 'ui-icons';
import { ColorWhite } from 'ui-connect/styles/default-theme';

/* utils */
import NavigationUtil from 'utils/NavigationUtil';
import AnalyticsUtil from 'utils/AnalyticsUtil';
import { getClassNames as cx } from 'utils/ReactUtil';
import viewerUserPreferences from 'models/user/ViewerUserPreferences';
import { trailsHash } from 'services/omt/subscription/subscriptionFields';

/* Styles */
import styles from './MenuItem.module.css';

const translationNamespaces = [
    'common',
    'connections',
    'widgets',
    'bodybattery',
    'fitness_age',
    'units',
    'device',
    'hydration',
    'bloodPressure',
    'menstrual-cycle-tracking',
    'main_container',
    'performance_dashboard',
    'ecg',
    'activity',
    'workout',
    'course',
    'jet_lag',
    'golf_main',
    'activity_types',
    trailsHash,
    'daily_summary',
    'notification',
    'power_guidance',
    'gear_tracking',
    'userprofile',
    'group',
    'trophy_case',
    'goals'
];

const MenuItem = ({
    title,
    translationNamespace,
    url,
    urlKeyword,
    icon,
    uiIcon,
    menuItems,
    disabled,
    iconColor,
    newsFeedStatus,
    isCollapsed,
    setCollapsed,
    isActionItem = false,
    isChild = false,
    target = '',
    forceClear = false
}) => {
    const { t, ready } = useTranslation(translationNamespaces);

    const [pathname, setPathname] = useState(window.location.pathname);
    const [showChildren, setShowChildren] = useState(false);
    const [showNewsFeedBubble, setShowNewsFeedBubble] = useState(true);

    const hasNewsfeedBubble = useMemo(
        () => title === 'news_feed' && newsFeedStatus && !!newsFeedStatus?.get('lastUnreadPostExpirationTimeGMT'),
        [title, newsFeedStatus]
    );
    const hasChildren = !!menuItems;

    const trackEvent = (key) => {
        AnalyticsUtil.push({
            event: 'track_event',
            category: 'Left Navigation Click Events',
            action: `User clicked ${key}`
        });
    };

    const handleOnClick = (event) => {
        if (title === 'news_feed') {
            setShowNewsFeedBubble(false);
            viewerUserPreferences.setNewsFeedLastReadTimeCookies();
        }

        if (!hasChildren) {
            if (!event.ctrlKey && !event.metaKey) {
                event.preventDefault();
            }
            event.stopPropagation();
            if (url.indexOf('https') !== -1) {
                window.open(url, '_blank');
            } else {
                NavigationUtil.anchorTagHandler(event);
                trackEvent(title);
            }

            setCollapsed(true);
        } else {
            setShowChildren((prev) => !prev);
        }
    };

    const menuIcon = useMemo(() => {
        if (!icon && !uiIcon) return null;
        if (!uiIcon) return <Icon name={icon} size={20} color={iconColor} />;

        return (
            <div className={styles.reactIcon}>
                <ChartsBar color={iconColor} size={20} />
            </div>
        );
    }, [icon, uiIcon, iconColor]);

    const isActive = useMemo(() => {
        if (!hasChildren) return (urlKeyword && pathname.includes(urlKeyword)) || pathname === `/modern${url}`;

        return menuItems.some((item) => pathname.indexOf(item.url || item.urlKeyword) !== -1);
    }, [hasChildren, menuItems, url, urlKeyword, pathname]);

    useEffect(() => {
        const updatePathname = () => {
            setPathname(window.location.pathname);
        };

        Backbone.history.on('route', updatePathname);

        return () => {
            Backbone.history.off('route', updatePathname);
        };
    }, []);

    return (
        // This prevents display only icons until we have all the translations fetched
        ready && (
            <li
                key={title}
                className={cx(
                    styles.menuItem,
                    isChild && styles.menuItemChild,
                    showChildren && !isCollapsed && styles.openMenuItem,
                    isCollapsed && styles.collapsed
                )}
            >
                <a
                    className={cx(
                        styles.menuItemLink,
                        uiIcon && styles.withIconUI,
                        disabled && styles.readOnly,
                        isChild && styles.menuItemLinkChild,
                        isActive && (isChild ? styles.activeChild : styles.active)
                    )}
                    href={url ? NavigationUtil.url(url) : null}
                    target={target}
                    onClick={handleOnClick}
                >
                    {menuIcon}
                    {hasNewsfeedBubble && showNewsFeedBubble && <span className={styles.newsfeedUnreadBubble} />}
                    <span
                        className={cx(
                            styles.menuItemText,
                            forceClear && styles.forceClear,
                            isActionItem && styles.actionItem
                        )}
                    >
                        {translationNamespace ? t(`${translationNamespace}:${title}`) : title}
                    </span>
                    {hasChildren && (
                        <Icon
                            className={`${styles.dropdownArrow} ${showChildren ? styles.open : ''} `}
                            name="arrow-down"
                            size={10}
                            color={ColorWhite}
                        />
                    )}
                </a>
                {hasChildren && (
                    <ul className={cx(styles.children, showChildren && !isCollapsed && styles.openChildren)}>
                        {menuItems.map((item) => (
                            <MenuItem
                                iconColor={item.color}
                                newsFeedStatus={newsFeedStatus}
                                {...item}
                                key={item.title}
                                isChild
                                isCollapsed={isCollapsed}
                                setCollapsed={setCollapsed}
                            />
                        ))}
                    </ul>
                )}
            </li>
        )
    );
};

export default memo(MenuItem);
